import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { SignupChallengeStep } from '@jerry-serverless/enums/user_challenge';

import type { User } from './user.fragment';
import { USER_FRAGMENT, USER_QUOTES_FRAGMENT } from './user.fragment';

export enum UserStageType {
  New = 'New',
  QuoteSent = 'QuoteSent',
  QuotingEZLynx = 'QuotingEZLynx',
  SwitchBinding = 'SwitchBinding',
  SwitchIntent = 'SwitchIntent',
  SwitchPriceProvided = 'SwitchPriceProvided',
  WaitingRequote = 'WaitingRequote',
  SwitchRequoting = 'SwitchRequoting',
}

export enum PolicyTypeType {
  Car = 'car',
  Home = 'home',
  Renter = 'renter',
  Condo = 'condo',
  Townhouse = 'townhouse',
}

export type NormalizedPolicyTypeType = PolicyTypeType.Car | PolicyTypeType.Home;

export enum PolicyTierType {
  Basic = 'basic',
  BasicPlus = 'basic_plus',
  Custom = 'custom',
  Predicted = 'predicted',
  Predicted2 = 'predicted2',
  Predicted3 = 'predicted3',
  Preferred = 'preferred',
  Standard = 'standard',
  Current = 'current',
}

export const tierTypeOrder: Record<PolicyTierType, number> = {
  [PolicyTierType.Custom]: 1,
  [PolicyTierType.Predicted]: 2,
  [PolicyTierType.Predicted2]: 3,
  [PolicyTierType.Predicted3]: 4,
  [PolicyTierType.Current]: 5,
  [PolicyTierType.Basic]: 6,
  [PolicyTierType.BasicPlus]: 7,
  [PolicyTierType.Standard]: 8,
  [PolicyTierType.Preferred]: 9,
};

export const tierLongTitles: Record<PolicyTierType, string> = {
  [PolicyTierType.Current]: 'Quotes comparable to your existing coverage',
  [PolicyTierType.Predicted]: 'Quotes with recommended coverage',
  [PolicyTierType.Predicted2]: 'Quotes with recommended coverage',
  [PolicyTierType.Predicted3]: 'Quotes with recommended coverage',
  [PolicyTierType.Basic]: 'Quotes that save you the most money with the coverage your state requires',
  [PolicyTierType.BasicPlus]: 'Quotes with minimum liability and full protection',
  [PolicyTierType.Standard]: 'Quotes that offer protection and savings',
  [PolicyTierType.Preferred]: 'Quotes with excellent coverage and protection',
  [PolicyTierType.Custom]: 'Quotes for coverages you chose',
};

export const tierShortTitles: Record<PolicyTierType, string> = {
  [PolicyTierType.Current]: 'Existing coverage',
  [PolicyTierType.Predicted]: 'Recommended plan',
  [PolicyTierType.Predicted2]: 'Recommended plan',
  [PolicyTierType.Predicted3]: 'Recommended plan',
  [PolicyTierType.Custom]: 'Custom plan',
  [PolicyTierType.Preferred]: 'Preferred plan',
  [PolicyTierType.Standard]: 'Standard plan',
  [PolicyTierType.Basic]: 'Basic plan',
  [PolicyTierType.BasicPlus]: 'Basic + full coverage plan',
};

export enum QuoteFailReasons {
  // Address is invalid
  AddressError = 'AddressError',
  // Credential supplied to the carrier is invalid, or it's for another product
  CarrierCredentialError = 'CarrierCredentialError',
  // Carrier system is in maintenance
  CarrierMaintenance = 'CarrierMaintenance',
  // Would include the Duck Creek Error, Object reference error
  CarrierSystemError = 'CarrierSystemError',
  // We have the wrong limit, coverage, coverage combination, or deductible for that carrier-state
  CoverageMapping = 'CoverageMapping',
  // Carrier does not support this coverage combination
  CoverageCombinationNotSupported = 'CoverageCombinationNotSupported',
  // Carrier can support this coverage but won't offer it
  CoverageNotOffered = 'CoverageNotOffered',
  // Carrier does not support this coverage
  CoverageNotSupported = 'CoverageNotSupported',
  // Policy date is invalid
  DateError = 'DateError',
  // Carrier refuses to quote due to delivery
  Delivery = 'Delivery',
  // Error generated by a rule we have not to display coverage. I.e. gold quote is returned without full coverage.
  DisplayRule = 'DisplayRule',
  // Carrier refuses to quote due to driver age
  DriverAge = 'DriverAge',
  // Carrier refuses to quote due to driver is rated as excluded
  DriverExcluded = 'DriverExcluded',
  // Carrier refuses to quote due to driver experience
  DriverExperience = 'DriverExperience',
  // Carrier refuses to quote due to driver license
  DriverLicense = 'DriverLicense',
  // Carrier refuses to quote due to inaccurate or incorrect driver partner or spouse info
  DriverSpouseOrPartner = 'DriverSpouseOrPartner',
  // Carrier refuses to quote due to driver with unknown reasons
  DriverUncategorized = 'DriverUncategorized',
  // Carrier refuses to quote due to invalid email address
  EmailAddressError = 'EmailAddressError',
  // EZLynx returns an error indicating the data is invalid
  EZLynxInvalidData = 'EZLynxInvalidData',
  // Carrier refuses to quote because the user has an active policy with the carrier
  HasActivePolicy = 'HasActivePolicy',
  // Carrier refuses to quote due to home quote not supported
  HomeQuoteNotSupported = 'HomeQuoteNotSupported',
  // Carrier returns an error indicating the data is invalid
  InvalidRequest = 'InvalidRequest',
  // Carrier refuses to quote due to invalid VIN
  InvalidVIN = 'InvalidVIN',
  // Error is driven by the Jerry quoting engine. Errors like "No matching quote from cache" would fit this type.
  JerryQuotingEngine = 'JerryQuotingEngine',
  // Carrier refuses to quote due to invalid lienholder coverage
  LienholderCoverageError = 'LienholderCoverageError',
  // Carrier refuses to quote due to invalid lienholder info
  LienholderInfoError = 'LienholderInfoError',
  // There was a network error
  NetworkError = 'NetworkError',
  // There was a network timeout
  NetworkTimeout = 'NetworkTimeout',
  // Carrier does not support the state
  NotSupportedStates = 'NotSupportedStates',
  // Carrier does not support to quote with this rater
  NotSupportedOnRater = 'NotSupportedOnRater',
  // Carrier does not support to quote due to unknown reasons
  NotSupportedUnknownReasons = 'NotSupportedUnknownReasons',
  // Rater system error
  RaterSystemError = 'RaterSystemError',
  // Carrier refuses to quote due to insurance score
  RejectUWInsuranceScore = 'RejectUWInsuranceScore',
  // Carrier refuses to quote due to unacceptable accidents
  RejectUnacceptableAccidents = 'RejectUnacceptableAccidents',
  // Carrier refuses to quote due to unacceptable violations
  RejectUnacceptableViolations = 'RejectUnacceptableViolations',
  // Carrier refuses to quote due to manual review required
  RejectManualReviewRequired = 'RejectManualReviewRequired',
  // Carrier refuses to quote due to unknown reasons
  RejectUncategorized = 'RejectUncategorized',
  // Carrier refuses to quote due to renter quote not supported
  RenterQuoteNotSupported = 'RenterQuoteNotSupported',
  // Carrier refuses to quote due to rideshare
  Rideshare = 'Rideshare',
  // Carrier couldn't return a quote due to temporary unavailability
  TemporarilyUnavailable = 'TemporarilyUnavailable',
  // A hack - DO NOT use this in categorization
  TotalCarrierQuotings = 'TotalCarrierQuotings',
  // Uncategorized error
  Uncategorized = 'Uncategorized',
  // Carrier never received this request because it's skipped on our end
  UncategorizedSkipped = 'UncategorizedSkipped',
  // Carrier failed to return a quote for unknown reasons
  UncategorizedFailed = 'UncategorizedFailed',
  // Carrier refuses to quote due to unacceptable vehicle
  VehicleUnacceptable = 'VehicleUnacceptable',
  // Carrier refuses to quote due to vehicle with unknown reasons
  VehicleUncategorized = 'VehicleUncategorized',
  // Carrier refuses to quote due to unacceptable vehicle usage
  VehicleUsage = 'VehicleUsage',
}

export interface PolicyCoverageType {
  assetID: string;
  ciName: string;
  ciValue: string;
  label: string;
}

export enum PaymentType {
  Installments = 0,
  PaidInFull = 1,
}

export enum PaymentMethod {
  CreditCard = 0,
  ElectronicFundsTransfer = 1,
  PayByMail = 2,
  MortgageBilled = 3,
  Stripe = 4,
}

export interface QuotePaymentPlansNode {
  id: string;
  term: number;
  premium: number;
  downPayment?: number;
  monthlyPayment?: number;
  monthlyPaymentCount?: number;
  paymentType: PaymentType;
  paymentMethod: PaymentMethod;
}

export interface QuotePaymentPlansType {
  node?: QuotePaymentPlansNode;
}

export interface QuotePaymentPlansEdge {
  edges: QuotePaymentPlansType[];
}

export interface PolicyType {
  id: string;
  type: PolicyTypeType;
  carrier: {
    id: string;
    name: string;
    nickname?: string;
    ratingValue?: number;
    ratingReviews?: number;
  };
  carrierName: string | null;
  premium: number | null;
  term: number | null;

  tier?: PolicyTierType;
  carIDs?: string[];
  driverIDs?: string[];
  additionalDriverIDs?: string[];
  coverage: {
    edges: Array<{ node: PolicyCoverageType }>;
  };
  userAddressSnapshot?: string;
  averagePriceByState?: number;
  averagePriceByCarrier?: number;
  quotePaymentPlans?: QuotePaymentPlansNode[];
  buyMetadata?: string;
}

export interface BundledQuotePolicyEdgeType {
  premium: number;
  policy: PolicyType;
}

export interface BundledQuoteType extends Record<string, unknown> {
  id: string;
  term: string;
  savings: number | null;
  policyEdges: BundledQuotePolicyEdgeType[];
}

export interface QuotesType {
  stage: UserStageType;
  currentBatchFinishedTiers?: PolicyTierType[];
  currentBatchCheckedCarriersCount?: number;
  currentBatchFailedReasons?: QuoteFailReasons[];
  currentQuotingProgress?: number | null;
  isQuotingInProgress?: boolean | null;
  bundledQuotes: BundledQuoteType[];
  activePolicies?: PolicyType[];
}

export interface UserWithQuotesType extends QuotesType, User {}

export interface QuotesQueryData {
  viewer: {
    user: QuotesType | null;
  };
}

export const USER_QUOTES_QUERY: TypedDocumentNode<{
  viewer: {
    user: UserWithQuotesType | null;
    signupChallengeStep: SignupChallengeStep;
  };
}> = gql`
  query AppViewer {
    viewer {
      signupChallengeStep
      user {
        ${USER_FRAGMENT}
        ${USER_QUOTES_FRAGMENT}
      }
    }
  }
`;

/**
 * This query returns the current logged in user with quotes.
 */
export const QUOTES_QUERY: TypedDocumentNode<{ viewer: { user: QuotesType | null } }> = gql`
  query AppViewer {
    viewer {
      user {
        ${USER_QUOTES_FRAGMENT}
      }
    }
  }
`;

/**
 * This query returns the current logged in user with quotes.
 */
export const PARTNER_QUOTES_QUERY: TypedDocumentNode<{ getPartnerUser: { user: QuotesType | null } }> = gql`
  query GetPartnerUser($pToken: String!) {
    getPartnerUser(pToken: $pToken) {
      user {
        ${USER_QUOTES_FRAGMENT}
      }
    }
  }
`;
